import { Component, HostListener, Input, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { Unsubscribe } from 'firebase/firestore';

import { AppHeaderService } from 'src/app/services/app-header/app-header.service';
import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { LocalStorageService } from 'src/app/services/local-storage/local-storage.service';
import { OrganizationService } from 'src/app/services/organization/organization.service';
import { UserService } from 'src/app/services/user/user.service';
import { ProfileService, SettingsTabs } from 'src/app/services/profile/profile.service';
import { FeatureSwitchService } from 'src/app/services/feature-switch/feature-switch.service';
import { SupportChatbotService } from 'src/app/components/support-chatbot/support-chatbot.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-icon-rail',
    templateUrl: './icon-rail.component.html',
    styleUrls: ['./icon-rail.component.scss'],
    standalone: false,
})
export class IconRailComponent implements OnInit, OnDestroy {
  @Input() mobileOpen = false;
  @Output() mobileOpenChange = new EventEmitter<boolean>();
  @Output() collapsedChange = new EventEmitter<boolean>();

  hovered = false;
  manualCollapsed = false;
  showProfilePopover = false;
  orgSubmenuOpen = false;
  productMenuOpen = false;
  otherOrganizations: { id: string; name: string }[] = [];

  enabledProducts: string[] = [];
  private productConfigUnsub: Unsubscribe | null = null;
  private subscribedOrgId: string | null = null;

  private readonly productDomain = environment.production ? 'crewdle.ai' : 'stg.crewdle.ai';
  /** All possible cross-product targets. Admin is always shown; others
   *  appear only when the org has them in `enabledProducts`. Connect itself
   *  is not in the list since this app *is* Connect. */
  private readonly allProductSwitcherEntries: { id: string; label: string; url: string }[] = [
    { id: 'admin', label: 'Admin', url: `https://admin.${this.productDomain}` },
    { id: 'chat', label: 'Chat', url: `https://chat.${this.productDomain}` },
    { id: 'build', label: 'Build', url: `https://build.${this.productDomain}` },
    { id: 'ai-studio', label: 'Forge', url: `https://forge.${this.productDomain}` },
  ];

  get productSwitcherItems(): { id: string; label: string; url: string }[] {
    const enabled = new Set(this.enabledProducts);
    return this.allProductSwitcherEntries.filter(
      (p) => p.id === 'admin' || enabled.has(p.id),
    );
  }

  constructor(
    public appHeader: AppHeaderService,
    public featureSwitch: FeatureSwitchService,
    public organization: OrganizationService,
    public profile: ProfileService,
    public user: UserService,
    private firebase: FirebaseService,
    private localStorage: LocalStorageService,
    private router: Router,
    private supportChatbot: SupportChatbotService,
  ) {}

  async ngOnInit(): Promise<void> {
    // OrganizationService.data is set imperatively after login finishes.
    // Wait for it to settle, then start listening for product-access config
    // updates. Org switches in this app trigger a full reload (see
    // switchOrganization), so the component remounts and re-runs ngOnInit
    // in that case — no need to subscribe to org changes here.
    await this.organization.isReady();
    this.refreshProductSubscription();
  }

  ngOnDestroy(): void {
    this.productConfigUnsub?.();
    this.productConfigUnsub = null;
  }

  private refreshProductSubscription(): void {
    const orgId = this.organization.data?.id ?? null;
    if (orgId === this.subscribedOrgId) return;
    this.productConfigUnsub?.();
    this.productConfigUnsub = null;
    this.subscribedOrgId = orgId;
    if (!orgId) {
      this.enabledProducts = [];
      return;
    }
    this.productConfigUnsub = this.firebase.firestore.docListen(
      `organizations/${orgId}/product-access/config`,
      (snap) => {
        const data: any = snap.exists() ? snap.data() : null;
        this.enabledProducts = Array.isArray(data?.enabledProducts) ? data.enabledProducts : [];
      },
      () => {
        // On error (e.g. missing rules access), just hide the optional products.
        this.enabledProducts = [];
      },
    );
  }

  openSupport(): void {
    this.supportChatbot.openPanel();
  }

  get expanded(): boolean {
    if (this.manualCollapsed && !this.mobileOpen && !this.hovered) return false;
    return this.mobileOpen || this.hovered || this.isDesktop;
  }

  toggleCollapse(): void {
    this.manualCollapsed = !this.manualCollapsed;
    this.collapsedChange.emit(this.manualCollapsed);
  }

  get isDesktop(): boolean {
    return typeof window !== 'undefined' && window.innerWidth >= 1024;
  }

  get isTablet(): boolean {
    return typeof window !== 'undefined' && window.innerWidth >= 768 && window.innerWidth < 1024;
  }

  get userName(): string {
    return this.user.data?.name || this.user.user?.displayName || '';
  }

  get userInitials(): string {
    const name = this.userName || this.user.data?.email || this.user.user?.email || '';
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  get sidebarClasses(): string {
    const base = 'sidebar';

    if (this.mobileOpen) {
      return `${base} sidebar-expanded sidebar-mobile-fullwidth`;
    }

    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return `${base} sidebar-expanded sidebar-hidden`;
    }

    if (this.isTablet || this.manualCollapsed) {
      return `${base} ${this.expanded ? 'sidebar-expanded' : 'sidebar-collapsed'}`;
    }

    return `${base} sidebar-expanded`;
  }

  navItemClass(active: boolean): string {
    const base = 'sidebar-nav-item';
    return active ? `${base} sidebar-nav-item-active` : base;
  }

  navigate(page: string): void {
    this.appHeader.navigateToPage(page);
    this.closeMobile();
  }

  isActive(tab: string): boolean {
    return this.appHeader.activeTab === tab;
  }

  onMouseEnter(): void {
    if (this.isTablet) {
      this.hovered = true;
    }
  }

  onMouseLeave(): void {
    if (this.isTablet) {
      this.hovered = false;
      this.closeAllMenus();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.showProfilePopover || this.productMenuOpen) {
      this.closeAllMenus();
    }
  }

  toggleProductMenu(event: Event): void {
    event.stopPropagation();
    this.productMenuOpen = !this.productMenuOpen;
  }

  selectProduct(product: { url: string }): void {
    this.productMenuOpen = false;
    window.location.href = product.url;
  }

  toggleProfilePopover(event: Event): void {
    event.stopPropagation();
    this.showProfilePopover = !this.showProfilePopover;
    if (this.showProfilePopover) {
      this.loadOtherOrganizations();
    }
    if (!this.showProfilePopover) {
      this.orgSubmenuOpen = false;
    }
  }

  goToAccount(): void {
    this.closeAllMenus();
    this.profile.navigateToProfile(SettingsTabs.you);
  }

  goToSettings(): void {
    this.closeAllMenus();
    this.profile.navigateToProfile();
  }

  switchOrganization(orgId: string): void {
    this.closeAllMenus();
    this.localStorage.setItem('preferredOrganizationId', orgId);
    // On /product-disabled the route has no productAccessGuard, so reloading
    // to the same URL leaves the user stranded even when the new org has
    // Connect enabled. Send them to '/' so the guard re-evaluates and either
    // lets them in or bounces back to /product-disabled.
    const path = window.location.pathname;
    const target = path === '/product-disabled' ? '/' : path;
    window.location.href = target;
  }

  logout(): void {
    this.closeAllMenus();
    this.user.logout();
  }

  closeMobile(): void {
    if (this.mobileOpen) {
      this.mobileOpenChange.emit(false);
    }
  }

  closeAllMenus(): void {
    this.showProfilePopover = false;
    this.orgSubmenuOpen = false;
    this.productMenuOpen = false;
    this.closeMobile();
  }

  private async loadOtherOrganizations(): Promise<void> {
    const orgIds = this.user.data?.organizationIds || [];
    const currentOrgId = this.organization.data?.id;
    const otherIds = orgIds.filter(id => id !== currentOrgId);
    if (!otherIds.length) {
      this.otherOrganizations = [];
      return;
    }

    const orgs: { id: string; name: string }[] = [];
    for (const id of otherIds) {
      try {
        const doc = await this.firebase.firestore.docGet(`organizations/${id}`);
        if (doc.exists()) {
          orgs.push({ id, name: doc.data()?.name || id });
        }
      } catch {
        // Skip orgs we can't read
      }
    }
    this.otherOrganizations = orgs;
  }
}
